import { isValidContactEmail, isValidRut } from './storage-profile-fields';

/**
 * NEX-1169 (plan 2026-10-20, Tarea 1.10) — the rules of the depósito's bank-account form
 * (`BankAccountForm.tsx`, `PATCH /storage/me/bank-account`). Client-side only, for immediate
 * feedback: the API's codes (`STORAGE_BANK_CODE_INVALID`, `STORAGE_BANK_ACCOUNT_NUMBER_INVALID`,
 * mapped in `storage-api.ts`'s `describeBankAccountError`) still decide a save.
 */

/**
 * The two account types, in the vocabulary the Itaú payout lane reads: `payment/domain/payout-lane.ts`
 * accepts `CC`/`CA` (its `RESOLVABLE_ACCOUNT_TYPES`) and the `.dat` writer reads `CA` as a caja de
 * ahorro — the same two codes the carrier catalog (`get-bank-account-options.handler.ts`) offers.
 */
export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'CA', label: 'Caja de ahorro' },
  { value: 'CC', label: 'Cuenta corriente' }
] as const;

/**
 * [ASSUMED] NEX-1169 — `RUT` / `CI`, not the carrier catalog's numeric codes ("1" … "9"): the Itaú
 * writer (`payout.service.ts`'s `docTypeCode`, which the storage payout batch reuses per the plan's
 * `itau-file.ts` extraction) only recognises the literal `RUT` (→ 9) and reads anything else as a
 * cédula (→ 1), so a "9" would put a company's RUT in the file as a cédula. A depósito invoices with
 * IVA (business doc §5), so RUT is the default; CI stays for a titular who is a person.
 */
export const DOCUMENT_TYPE_OPTIONS = [
  { value: 'RUT', label: 'RUT' },
  { value: 'CI', label: 'Cédula de identidad' }
] as const;

/** `PATCH /storage/me/bank-account`'s `paymentMethod`: the entity's own default, sent explicitly. */
export const BANK_TRANSFER_PAYMENT_METHOD = 'bank_transfer';

/** `storage_bank_accounts`' column widths, as `maxLength` on the inputs (a longer value is a 500). */
export const BANK_FIELD_MAX_LENGTH = { beneficiaryName: 100, documentNumber: 50, accountNumber: 50, bankEmail: 100 } as const;

export function accountTypeLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return ACCOUNT_TYPE_OPTIONS.find((option) => option.value === code.trim().toUpperCase())?.label ?? code;
}

export function documentTypeLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return DOCUMENT_TYPE_OPTIONS.find((option) => option.value === code.trim().toUpperCase())?.label ?? code;
}

/** "•••• 1234": only the last four digits of a saved account are shown. */
export function maskAccountNumber(accountNumber: string | null | undefined): string {
  const value = (accountNumber ?? '').trim();
  if (!value) return '—';
  if (value.length <= 4) return '•'.repeat(value.length);
  return `•••• ${value.slice(-4)}`;
}

/** Account and document numbers are digits: spaces, dots and dashes people type are dropped. */
export function normalizeDigits(value: string): string {
  return value.replace(/[\s.-]/g, '');
}

export interface BankAccountFormValues {
  bankCode: string;
  bankAccountType: string;
  accountNumber: string;
  beneficiaryName: string;
  documentType: string;
  documentNumber: string;
  /** Optional: the API accepts it and the Itaú file can notify it. */
  bankEmail: string;
}

export type BankAccountFormErrors = Partial<Record<keyof BankAccountFormValues, string>>;

const DIGITS = /^\d+$/;

/**
 * Every field the `storage_bank_accounts` row needs (all `NOT NULL` but the email), plus the formats
 * every bank rule shares: the account number is digits (each `BANK_ACCOUNT_RULES` entry of the
 * handler is), a RUT is 12 digits (`storage-profile-fields.ts`'s rule).
 */
export function validateBankAccount(values: BankAccountFormValues): BankAccountFormErrors {
  const errors: BankAccountFormErrors = {};
  if (!values.bankCode) errors.bankCode = 'Elegí el banco.';
  if (!values.bankAccountType) errors.bankAccountType = 'Elegí el tipo de cuenta.';

  const accountNumber = normalizeDigits(values.accountNumber);
  if (!accountNumber) errors.accountNumber = 'Ingresá el número de cuenta.';
  else if (!DIGITS.test(accountNumber)) errors.accountNumber = 'Ingresá solo los números de la cuenta.';

  if (!values.beneficiaryName.trim()) errors.beneficiaryName = 'Ingresá el titular de la cuenta.';
  if (!values.documentType) errors.documentType = 'Elegí el tipo de documento.';

  const documentNumber = normalizeDigits(values.documentNumber);
  if (!documentNumber) errors.documentNumber = 'Ingresá el número de documento.';
  else if (!DIGITS.test(documentNumber)) errors.documentNumber = 'Ingresá solo los números del documento.';
  else if (values.documentType === 'RUT' && !isValidRut(documentNumber)) {
    errors.documentNumber = 'El RUT tiene que tener exactamente 12 dígitos.';
  }

  if (values.bankEmail.trim() && !isValidContactEmail(values.bankEmail)) errors.bankEmail = 'Ese correo no es válido.';
  return errors;
}
